import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getOrderInProgress, hasCardPaymentInProgress } from '../../selectors/order'
import { isTabbed } from '../../utils/orderStates'
import {
  getCashEnabled,
  getGiftCardEnabled,
  getMobilePaySetting,
  getTicketEnabled,
  makeGetNoOpTenders,
} from '../../selectors/revenueCenter'
import { PAYMENT_TYPES_INTERNAL } from '../../utils/paymentTypes'
import { reduce } from '../../VNUtil/VNUtils'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { getTabsModeEnabled } from '../../selectors/menus'
import { isSDKCompatible } from '../../VNAndroidSDK/Utils'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { MOBILE_PAY_SCAN_QR, MOBILE_PAY_SHOW_QR, VERSION_MIN_FOR_SPLIT } from '../Utils'

export const useTenders = () => {
  const order = useSelector((state) => getOrderInProgress(state))
  const cfdMode = useSelector((state) => getDeviceMode(state))
  const androidSDKVersion = useSelector((state) => getAndroidSDKVersion(state))
  const hasCardAuth = useSelector((state) => hasCardPaymentInProgress(state))
  const cashEnabled = useSelector((state) => getCashEnabled(state))
  const giftCardEnabled = useSelector((state) => getGiftCardEnabled(state))
  const mobilePaySetting = useSelector((state) => getMobilePaySetting(state))
  const noOpTenders = useSelector((state) => makeGetNoOpTenders()(state))
  const tabsModeEnabled = useSelector((state) => getTabsModeEnabled(state))
  const ticketEnabled = useSelector((state) => getTicketEnabled(state))

  /**
   * Formats currently selected payment
   */
  const getSavedPaymentAsTender = useCallback(() => {
    const prefix = order.paymentDisplay || order?.cardReaderData?.cardIssuer
    let suffix = ''

    if (order?.cardReaderData?.cardholderName) {
      suffix = `- ${order.cardReaderData.cardholderName}`
    }

    return [PAYMENT_TYPES_INTERNAL.SAVED_CARD, `${prefix} ${suffix}`, false]
  }, [order])

  const isSplitEnabled = useCallback(
    (fromSplit) => {
      if (fromSplit) return false
      if (!tabsModeEnabled) return false

      return cfdMode !== MODES.POS || isSDKCompatible(androidSDKVersion, VERSION_MIN_FOR_SPLIT)
    },
    [androidSDKVersion, cfdMode, tabsModeEnabled],
  )

  /**
   * Tender Selection for Pay Now, Tabs, Splits and non-BackBar Use Cases
   */
  const getAvailableTenders = useCallback(
    (fromSplit = false, isTicketScreen = false) => {
      const isNewOrder = !Object.keys(order)?.length
      const isTabOrder = isTabbed(order)
      const showAllPayments = isNewOrder || isTabOrder
      let paymentTypes = []

      // Split Payment Option at the top
      if (isSplitEnabled(fromSplit)) {
        paymentTypes.push(['split', 'Split', false])
      }

      // Saved Card - Auth'ed Card cannot be use in split
      if (hasCardAuth && isTabbed(order) && !fromSplit) {
        paymentTypes.push(getSavedPaymentAsTender())
      }

      // Custom Tenders
      paymentTypes = reduce(
        noOpTenders,
        (payment, tender) => {
          return [...payment, [tender.name, tender.displayName, tender.managerApproval]]
        },
        paymentTypes,
      )

      // card is always an option
      paymentTypes.push([PAYMENT_TYPES_INTERNAL.CREDIT_CARD, 'Card'])

      // cash
      if (cashEnabled) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.CASH, 'Cash', false])
      }

      if (fromSplit) {
        return paymentTypes
      }

      if (!showAllPayments) {
        // Enable Ticket on ticket screen
        if (isTicketScreen && ticketEnabled) {
          paymentTypes.push([PAYMENT_TYPES_INTERNAL.TICKET, 'Ticket', false])
        }
        return paymentTypes
      }

      if (mobilePaySetting === MOBILE_PAY_SHOW_QR) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.VIRTUAL_CURRENCY, 'App', false])
      }

      if (mobilePaySetting === MOBILE_PAY_SCAN_QR) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.QR_PAY, 'QR Pay', false])
      }

      // ticket enabled for showAllPayments
      if (ticketEnabled) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.TICKET, 'Ticket', false])
      }

      if (giftCardEnabled) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.GIFT_CARD, 'Gift Card', false])
      }

      return paymentTypes
    },
    [
      cashEnabled,
      getSavedPaymentAsTender,
      giftCardEnabled,
      hasCardAuth,
      isSplitEnabled,
      mobilePaySetting,
      noOpTenders,
      order,
      ticketEnabled,
    ],
  )

  /**
   * Tender Selection as Required for BackBar
   * Options include: Auth'ed (saved) card, custom tenders, card and cash
   */
  const getBackBarTenders = useCallback(() => {
    let paymentTypes = []

    // currently pre-authorized Card
    if (hasCardAuth && isTabbed(order)) {
      paymentTypes.push(getSavedPaymentAsTender())
    }

    // custom tenders
    paymentTypes = reduce(
      noOpTenders,
      (tenders, tender) => {
        return [...tenders, [tender.name, tender.displayName, tender.managerApproval]]
      },
      paymentTypes,
    )

    // card is always an option
    paymentTypes.push([PAYMENT_TYPES_INTERNAL.CREDIT_CARD, 'Card'])

    // cash
    if (cashEnabled) {
      paymentTypes.push([PAYMENT_TYPES_INTERNAL.CASH, 'Cash', false])
    }

    return paymentTypes
  }, [cashEnabled, getSavedPaymentAsTender, hasCardAuth, noOpTenders, order])

  return {
    getAvailableTenders,
    getBackBarTenders,
  }
}

export default useTenders
